import { ChangeEvent, FormEvent, useState } from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useSnackbar } from "notistack";
import ButtonLoading from "../../../Button/ButtonLoading";
import ContentBox from "../../../ContentBox/ContentBox";
import { apiPost } from "../../../../services/Api/apiCall";
import { ResponseError } from "../../../../services/ApiClient";

type BuschbellTestMailData = {
  insurancePolicyNumber: string;
  familyName: string;
  givenName: string;
  phone: string;
  email: string;
  description: string;
  legalAdvice: string;
};

const initialFormData: BuschbellTestMailData = {
  insurancePolicyNumber: "",
  familyName: "",
  givenName: "",
  phone: "",
  email: "",
  description: "",
  legalAdvice: "",
};

export default function BuschbellTestMail() {
  const [formData, setFormData] = useState<BuschbellTestMailData>(initialFormData);
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const subject = `ZK LVM BUSCHBELL ${formData.insurancePolicyNumber}, ${formData.familyName}, ${formData.givenName}`;

  const changeField = (field: keyof BuschbellTestMailData) => (event: ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const sendTestMail = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      await apiPost("email/buschbell_test_mail", formData);
      setFormData(initialFormData);
      enqueueSnackbar("Die Buschbell-Testmail wurde an bcc-api-testing@legalbird.de versendet.", {
        variant: "custom",
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    } catch (error) {
      let errorMessage = "Die Buschbell-Testmail konnte nicht versendet werden.";
      if (error instanceof ResponseError && error.status === 403) {
        errorMessage = "Deine Backoffice-E-Mail-Adresse ist nicht als LVM-Buschbell-Testabsender konfiguriert.";
      } else if (error instanceof ResponseError && error.status === 400) {
        errorMessage = "Bitte prüfe die eingegebenen Daten.";
      }

      enqueueSnackbar(errorMessage, {
        variant: "error",
        isNonInteractive: true,
        anchorOrigin: {
          vertical: "bottom",
          horizontal: "center",
        },
      });
    }

    setIsLoading(false);
  };

  return (
    <ContentBox headline="LVM-Buschbell-Testmail erstellen">
      <Typography sx={{ marginBottom: "1rem" }}>
        Erstellt eine Mail im Buschbell-Format und sendet sie von deiner Backoffice-E-Mail-Adresse an das
        BCC-API-Testpostfach. Der Versand legt bei erfolgreicher Verarbeitung einen echten Testfall an.
      </Typography>
      <Grid component="form" container spacing={2} onSubmit={sendTestMail}>
        <Grid size={12}>
          <TextField
            fullWidth
            label="Betreff"
            value={subject}
            slotProps={{ input: { readOnly: true } }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            required
            label="Versicherungsnummer (VSNR)"
            placeholder="81.514.329.9"
            value={formData.insurancePolicyNumber}
            onChange={changeField("insurancePolicyNumber")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            required
            label="Telefonnummer"
            type="tel"
            value={formData.phone}
            onChange={changeField("phone")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            required
            label="Nachname"
            value={formData.familyName}
            onChange={changeField("familyName")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            required
            label="Vorname"
            value={formData.givenName}
            onChange={changeField("givenName")}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            required
            label="E-Mail-Adresse des Versicherungsnehmers"
            type="email"
            value={formData.email}
            onChange={changeField("email")}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            required
            multiline
            minRows={3}
            label="Sachverhalt"
            value={formData.description}
            onChange={changeField("description")}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            required
            label="Rechtsrat"
            value={formData.legalAdvice}
            onChange={changeField("legalAdvice")}
          />
        </Grid>
        <Grid size={12}>
          <ButtonLoading type="submit" variant="contained" isLoading={isLoading}>
            Testmail senden
          </ButtonLoading>
        </Grid>
      </Grid>
    </ContentBox>
  );
}
